import type { Notification } from '@/types'

/**
 * High-level categories used by the Notifications page type filter. Each backend
 * notification `type` maps to exactly one category; unknown types fall back to
 * `system` so the filter never silently drops a notification.
 */
export type NotificationCategory =
  | 'shifts'
  | 'attendance'
  | 'leave'
  | 'payroll'
  | 'approvals'
  | 'account'
  | 'announcements'
  | 'messages'
  | 'system'

interface CategoryMeta {
  value: NotificationCategory
  label: string
}

/** Ordered for display in the type filter dropdown. */
export const NOTIFICATION_CATEGORIES: CategoryMeta[] = [
  { value: 'shifts', label: 'Shift updates' },
  { value: 'attendance', label: 'Clock-in & attendance' },
  { value: 'leave', label: 'Leave requests' },
  { value: 'payroll', label: 'Payroll & pay' },
  { value: 'approvals', label: 'Approvals' },
  { value: 'account', label: 'Account & security' },
  { value: 'announcements', label: 'Announcements' },
  { value: 'messages', label: 'Messages' },
  { value: 'system', label: 'System' },
]

const TYPE_TO_CATEGORY: Record<string, NotificationCategory> = {
  SHIFT_ASSIGNED: 'shifts',
  SHIFT_EXTENDED: 'shifts',
  SHIFT_UPDATED: 'shifts',
  SHIFT_REMOVED: 'shifts',
  SHIFT_ALERT: 'shifts',
  SHIFT_ALERT_ESCALATION: 'shifts',
  SHIFT_CLOCK_IN: 'attendance',
  LEAVE_REQUEST_SUBMITTED: 'leave',
  LEAVE_APPROVED: 'leave',
  LEAVE_REJECTED: 'leave',
  PENALTY_ISSUED: 'payroll',
  BONUS_ISSUED: 'payroll',
  SECURITY_DEPOSIT_DEDUCTION: 'payroll',
  SECURITY_REFUND_PROCESSED: 'payroll',
  PAYROLL_READY: 'payroll',
  PAYROLL_DRAFT_CREATED: 'payroll',
  PAYROLL_CALCULATED: 'payroll',
  PAYROLL_COMPANY_APPROVED: 'payroll',
  PAYROLL_BATCH_APPROVED: 'payroll',
  PAYROLL_BATCH_PAID: 'payroll',
  PAYROLL_BATCH_REJECTED: 'payroll',
  PAYROLL_RUN_REJECTED: 'payroll',
  PAYROLL_CALCULATION_ERRORS: 'payroll',
  PAYROLL_ADJUSTMENT: 'payroll',
  SALARY_SLIP_AVAILABLE: 'payroll',
  APPROVAL_PENDING: 'approvals',
  EXPENSE_CLAIM_SUBMITTED: 'approvals',
  EXPENSE_CLAIM_APPROVED: 'payroll',
  EXPENSE_CLAIM_REJECTED: 'payroll',
  EXPENSE_CLAIM_REIMBURSED: 'payroll',
  ROLE_CHANGED: 'account',
  ACCOUNT_STATUS_CHANGED: 'account',
  PASSWORD_RESET: 'account',
  USER_INVITED: 'account',
  NEW_DEVICE_SIGN_IN: 'account',
  ACCOUNT_LOCKED: 'account',
  PASSWORD_CHANGED: 'account',
  ANNOUNCEMENT_PUBLISHED: 'announcements',
  CHANNEL_MESSAGE: 'messages',
  DIRECT_MESSAGE: 'messages',
  SUPPORT_TICKET_UPDATE: 'messages',
}

export function getNotificationCategory(n: Notification): NotificationCategory {
  return TYPE_TO_CATEGORY[n.type] ?? 'system'
}

const CATEGORY_LABEL = new Map(
  NOTIFICATION_CATEGORIES.map((c) => [c.value, c.label])
)

export function getNotificationCategoryLabel(
  category: NotificationCategory
): string {
  return CATEGORY_LABEL.get(category) ?? 'System'
}

/** Friendly label for a raw notification `type` (e.g. for the Type column). */
export function formatNotificationType(type: string): string {
  if (!type) return 'Notification'
  return type
    .toLowerCase()
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ')
}
